import { useState } from 'react';
import QuickActionCard, {
  AddCard,
} from '../../../components/card/quick-action-card/quick-action-card.component';
import { Select, SelectItem } from 'carbon-components-react';
import { useUrlState } from '../../../utils/useUrlState';
import useSWR from 'swr';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import {
  formatCurrencyV2,
  getItemById,
  getQueryString,
} from '../../../utils/common.util';
import CopyAidProgramsModal from './CopyAidProgramsModal';
import { PrintView } from '../../../components/PrintView/print-view.component';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import { Printer } from 'react-feather';
import PrintOutHead from '../../../components/PrintOutHead';
import PageLayout from '@/components/PageLayout';
import PageGuard from '@/components/page-guard/PageGuard';
import { BILLING_SUB_ROLES } from '@/utils/user-roles';
import ComponentGuard from '@/components/component-guard/ComponentGuard';
import useCurrentAcademicTerm from '@/utils/useCurrentAcademicTerm';

const initialData = {
  academicYearId: '',
  termId: '',
  aidType: '',
  isActive: '',
};

export const formatAmount = (program) => {
  if (program.amountType === 'Percentage') {
    return `${program.amount}%`;
  }
  return `${formatCurrencyV2(program.amount)}`;
};

export default function FinancialAidPage() {
  const [query, setQuery] = useUrlState(initialData);
  const history = useHistory();
  const [isRolloverModalOpen, setIsRolloverModalOpen] = useState(false);

  const {
    academicYears,
    terms,
    currentAcademicYear,
    currentTerm,
  } = useCurrentAcademicTerm({
    academicYearId: query.academicYearId,
    setQuery,
  });

  const displayAcademicYearId =
    query.academicYearId || currentAcademicYear?.id?.toString() || '';

  const displayTermId = query.termId || currentTerm?.id?.toString() || '';

  // Build the query key for programs
  const programsKey = query.academicYearId
    ? `financial-aid/programs?${getQueryString({
        academicYearId: query.academicYearId,
        termId: query.termId || undefined,
        type: query.aidType || undefined,
        isActive:
          query.isActive !== '' ? query.isActive : undefined,
      })}`
    : null;

  const {
    data: programs,
    isLoading: isLoadingPrograms,
    mutate,
  } = useSWR(programsKey);

  const handleAcademicYearChange = (e) => {
    const newAcademicYearId = e.target.value;
    setQuery({
      academicYearId: newAcademicYearId,
      termId: '',
      aidType: query.aidType,
      isActive: query.isActive,
    });
  };

  const handleTermChange = (e) => {
    setQuery({
      ...query,
      termId: e.target.value,
    });
  };

  const handleAidTypeChange = (e) => {
    setQuery({
      ...query,
      aidType: e.target.value,
    });
  };

  const handleActiveChange = (e) => {
    setQuery({
      ...query,
      isActive: e.target.value,
    });
  };

  return (
    <PageGuard
      allowedRoles={[
        BILLING_SUB_ROLES.FINANCIAL_AID_ACCESS,
        BILLING_SUB_ROLES.FINANCIAL_AID_ACCESS_READ_ONLY,
      ]}
    >
      <PageLayout
        title="Financial Aid Programs"
        breadcrumbs={[
          { label: 'Billing', to: '/billing' },
          { label: `Financial Aid Programs`, active: true },
        ]}
        isLoading={isLoadingPrograms}
      >
        <div className="card-row-3">
          <ComponentGuard
            allowedRoles={[BILLING_SUB_ROLES.FINANCIAL_AID_ACCESS]}
          >
            <AddCard
              title="CREATE A PROGRAM"
              onClick={() => history.push('/financial-aid/create')}
            />

            <QuickActionCard
              description="Term Roll Over"
              leftActionText="VIEW"
              actionEvent1={() => setIsRolloverModalOpen(true)}
              hideAction2
            />
          </ComponentGuard>

          <QuickActionCard
            description="Reports"
            leftActionText="VIEW"
            actionEvent1={() => history.push('/financial-aid/reports')}
            hideAction2
          />
        </div>

        <div className="card-row-4 mt-30">
          <Select
            labelText="Aid Type"
            onChange={handleAidTypeChange}
            value={query.aidType || ''}
          >
            <SelectItem value="" text="All" />
            {['Scholarship', 'Discount', 'Waiver']?.map((type) => (
              <SelectItem key={type} value={type} text={type} />
            ))}
          </Select>
          <Select
            labelText="Academic Year"
            onChange={handleAcademicYearChange}
            value={displayAcademicYearId}
            disabled={!academicYears}
          >
            {/* <SelectItem value="" text="All" /> */}
            {academicYears?.map((year) => (
              <SelectItem
                key={year.id}
                value={year.id}
                text={year.description}
              />
            ))}
          </Select>
          <Select
            labelText="Term"
            disabled={!displayAcademicYearId}
            onChange={handleTermChange}
            value={displayTermId}
          >
            <SelectItem value="" text="All" />
            {terms?.map((term) => (
              <SelectItem
                key={term.id}
                value={term.id}
                text={term.description}
              />
            ))}
          </Select>
          <Select
            labelText="Status"
            onChange={handleActiveChange}
            value={query.isActive || ''}
          >
            <SelectItem value="" text="All" />
            <SelectItem value="true" text="Active" />
            <SelectItem value="false" text="Inactive" />
          </Select>
        </div>

        <div className="mt-30">
          <PrintOutHead
            title={`Financial Aid Programs`}
            headings={[
              {
                label: 'Aid Type',
                value: query.aidType || 'All',
              },
              {
                label: 'Academic Year',
                value:
                  getItemById(academicYears, query.academicYearId)
                    ?.description ?? 'All',
              },
              {
                label: 'Term',
                value: getItemById(terms, query.termId)?.description ?? 'All',
              },
              {
                label: 'Status',
                value:
                  query.isActive === ''
                    ? 'All'
                    : query.isActive === 'true'
                      ? 'Active'
                      : 'Inactive',
              },
            ]}
          />
          {!displayAcademicYearId ? (
            <div
              style={{
                padding: '40px',
                textAlign: 'center',
                backgroundColor: '#f4f4f4',
                borderRadius: '4px',
              }}
            >
              <p style={{ color: '#666' }}>
                Please select an Academic Year to view financial aid programs.
              </p>
            </div>
          ) : isLoadingPrograms ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              Loading programs...
            </div>
          ) : programs && programs.length > 0 ? (
            <PrintView>
              <table className="bx--data-table table-white">
                <thead>
                  <tr>
                    <th>Program Name</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Academic Year</th>
                    <th>Term</th>
                    <th>Bill Items Linked</th>
                    <th>Students Assigned</th>
                    <th>Status</th>
                    <th className="no-print">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {programs.map((program) => (
                    <tr key={program.id}>
                      <td>{program.name}</td>
                      <td>{program.type}</td>
                      <td>{formatAmount(program)}</td>
                      <td>{program.academicYearDescription}</td>
                      <td>{program.termDescription || '-'}</td>
                      <td>{program.billItems?.length || 0}</td>
                      <td>{program.studentCount || 0}</td>
                      <td>
                        <span
                          style={{
                            color: program.isActive ? '#24a148' : '#6f6f6f',
                            fontWeight: 'bold',
                          }}
                        >
                          {program.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={() =>
                            history.push(`/financial-aid/single/${program.id}`)
                          }
                          className="bx--btn bx--btn--ghost"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </PrintView>
          ) : (
            <div
              style={{
                padding: '40px',
                textAlign: 'center',
                backgroundColor: '#f4f4f4',
                borderRadius: '4px',
              }}
            >
              <p style={{ color: '#666' }}>
                No financial aid programs found for the selected filters.
              </p>
            </div>
          )}
          {programs?.length > 0 && (
            <div className="mt-30 d-flex gap-20 align-items-center flex-end">
              <CustomButton classes="secondary" onClick={() => window.print()}>
                <Printer size="16" strokeWidth="2px" /> &nbsp; Print
              </CustomButton>
            </div>
          )}
        </div>
      </PageLayout>

      <CopyAidProgramsModal
        isOpen={isRolloverModalOpen}
        onClose={() => setIsRolloverModalOpen(false)}
        onSuccess={() => mutate()}
      />
    </PageGuard>
  );
}
